import { PERSIST_KEY } from "./store";

/**
 * Removes all Redux persist data for a specific tab session ID.
 * This includes data from all apps (main, flight, etc.) that share the same tabSessionId.
 *
 * @param tabSessionId - The tab session ID to clean up
 */
export function cleanupReduxForTab(tabSessionId: string): void {
  if (!tabSessionId || typeof window === "undefined") {
    return;
  }

  try {
    // Redux-persist adds "persist:" prefix, then our custom storage adds ":tabSessionId"
    // Final format: "persist:redux:1234567890"
    const tabSpecificKey = `persist:${PERSIST_KEY}:${tabSessionId}`;

    localStorage.removeItem(tabSpecificKey);
  } catch {
    // Cleanup failed silently
  }
}

/**
 * Removes all orphaned Redux persist data from localStorage.
 * This is useful for cleaning up data from tabs that were closed unexpectedly.
 *
 * @param activeTabs - Array of currently active tab session IDs
 */
export function cleanupOrphanedReduxData(activeTabs: string[]): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    // The actual key format is "redux:<tabSessionId>" (no "persist:" prefix)
    const prefix = `${PERSIST_KEY}:`;
    const keysToRemove: string[] = [];

    // Find all Redux persist keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        // Extract the tabSessionId from the key
        const tabSessionId = key.replace(prefix, "");

        // If this tab is not in the active tabs list, mark for removal
        if (!activeTabs.includes(tabSessionId)) {
          keysToRemove.push(key);
        }
      }
    }

    // Remove orphaned keys
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error("[Redux Cleanup] Failed to clean up orphaned Redux data:", error);
  }
}
